/**
 * Task-focused data model utilities
 * Transforms orders into task-centric data structure for the Dashboard
 * Ready to be wired to backend APIs later
 */

import { 
  TASK_STATUS, 
  STATUS_DISPLAY, 
  normalizeStatus as normalizeStatusFromConstants,
  getProgressFromStatus 
} from '../constants/statusConstants';

// Re-export canonical statuses - use TASK_STATUS directly
export { TASK_STATUS };

// Blocker types
export const BLOCKER_TYPES = {
  USER: 'user',
  EXPERT: 'expert',
  AUTHORITY: 'authority',
  NONE: null
};

// Status display config - using canonical values
export const STATUS_CONFIG = {
  [TASK_STATUS.INTAKE]: {
    label: 'Intake',
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    dotColor: 'bg-blue-500',
    description: 'Gathering your information'
  },
  [TASK_STATUS.REVIEW]: {
    label: 'Review',
    color: 'bg-violet-100 text-violet-700 border-violet-200',
    dotColor: 'bg-violet-500',
    description: 'Being reviewed by our experts'
  },
  [TASK_STATUS.FILING]: {
    label: 'Filing',
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    dotColor: 'bg-amber-500',
    description: 'Submitting to authorities'
  },
  [TASK_STATUS.WAITING]: {
    label: 'Waiting',
    color: 'bg-orange-100 text-orange-700 border-orange-200',
    dotColor: 'bg-orange-500',
    description: 'Waiting for authority response'
  },
  [TASK_STATUS.DONE]: {
    label: 'Done',
    color: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    dotColor: 'bg-emerald-500',
    description: 'Successfully completed'
  },
  [TASK_STATUS.CANCELLED]: {
    label: 'Cancelled',
    color: 'bg-slate-100 text-slate-700 border-slate-200',
    dotColor: 'bg-slate-400',
    description: 'Task was cancelled'
  }
};

// Blocker display config
export const BLOCKER_CONFIG = {
  [BLOCKER_TYPES.USER]: {
    label: 'Action Required',
    color: 'bg-red-50 text-red-700 border-red-200',
    icon: 'user'
  },
  [BLOCKER_TYPES.EXPERT]: {
    label: 'With Expert',
    color: 'bg-blue-50 text-blue-700 border-blue-200',
    icon: 'expert'
  },
  [BLOCKER_TYPES.AUTHORITY]: {
    label: 'With Authority',
    color: 'bg-amber-50 text-amber-700 border-amber-200',
    icon: 'building'
  }
};

// Service-specific deadline rules (days from task creation)
const SERVICE_DEADLINES = {
  'GST Registration': { expectedDays: 5, filingDeadline: 7 },
  'Monthly GST Filing': { expectedDays: 3, filingDeadline: 11 }, // 11th of next month
  'GST Annual Return': { expectedDays: 7, filingDeadline: 31 },
  'Private Limited Company': { expectedDays: 10, filingDeadline: 15 },
  'LLP Registration': { expectedDays: 10, filingDeadline: 15 },
  'Partnership Firm': { expectedDays: 7, filingDeadline: 10 },
  'Proprietorship': { expectedDays: 5, filingDeadline: 7 },
  'ITR Filing - Individual': { expectedDays: 3, filingDeadline: 5 },
  'ITR Filing - Business': { expectedDays: 7, filingDeadline: 10 },
  'TDS Return Filing': { expectedDays: 5, filingDeadline: 7 },
  'Trademark Registration': { expectedDays: 14, filingDeadline: 30 },
  'Copyright Registration': { expectedDays: 10, filingDeadline: 20 },
  'Patent Filing': { expectedDays: 30, filingDeadline: 60 },
  'Annual ROC Filing': { expectedDays: 7, filingDeadline: 14 },
  'DIN eKYC': { expectedDays: 2, filingDeadline: 3 },
  'Board Resolution': { expectedDays: 2, filingDeadline: 3 }
};

// Default deadline config
const DEFAULT_DEADLINE = { expectedDays: 7, filingDeadline: 14 };

// Required documents by service type
const SERVICE_DOCUMENTS = {
  'GST Registration': [
    { name: 'PAN Card', required: true },
    { name: 'Aadhaar Card', required: true },
    { name: 'Address Proof', required: true },
    { name: 'Bank Statement', required: true },
    { name: 'Photograph', required: true }
  ],
  'Private Limited Company': [
    { name: 'Director PAN Cards', required: true },
    { name: 'Director Aadhaar Cards', required: true },
    { name: 'Address Proof (Directors)', required: true },
    { name: 'Registered Office Proof', required: true },
    { name: 'NOC from Owner', required: false },
    { name: 'Utility Bill', required: true }
  ],
  'LLP Registration': [
    { name: 'Partner PAN Cards', required: true },
    { name: 'Partner Aadhaar Cards', required: true },
    { name: 'Address Proof (Partners)', required: true },
    { name: 'Registered Office Proof', required: true },
    { name: 'NOC from Owner', required: false }
  ],
  'ITR Filing - Individual': [
    { name: 'Form 16', required: true },
    { name: 'Bank Statements', required: true },
    { name: 'Investment Proofs', required: false },
    { name: 'Previous ITR', required: false }
  ],
  'ITR Filing - Business': [
    { name: 'P&L Statement', required: true },
    { name: 'Balance Sheet', required: true },
    { name: 'Bank Statements', required: true },
    { name: 'GST Returns', required: false },
    { name: 'Previous ITR', required: false }
  ],
  'Trademark Registration': [
    { name: 'Logo/Wordmark', required: true },
    { name: 'Business Proof', required: true },
    { name: 'ID Proof', required: true },
    { name: 'Address Proof', required: true }
  ]
};

const DEFAULT_DOCUMENTS = [
  { name: 'ID Proof', required: true },
  { name: 'Address Proof', required: true },
  { name: 'Business Documents', required: false }
];

/**
 * Map order status to task status using canonical values
 */
function mapOrderStatusToTaskStatus(orderStatus) {
  // Use the normalizer from statusConstants to handle all cases
  return normalizeStatusFromConstants(orderStatus);
}

/**
 * Determine blocker based on task status and documents
 */
function determineBlocker(status, documents, requiredDocs) {
  if (status === TASK_STATUS.DONE || status === TASK_STATUS.CANCELLED) {
    return BLOCKER_TYPES.NONE;
  }

  // Check if user needs to upload documents
  if (status === TASK_STATUS.INTAKE) {
    const uploadedNames = documents.map(d => d.file_name?.toLowerCase() || '');
    const missingRequired = requiredDocs
      .filter(d => d.required)
      .some(d => !uploadedNames.some(name => name.includes(d.name.toLowerCase().split(' ')[0])));
    
    if (missingRequired) {
      return BLOCKER_TYPES.USER;
    }
  }

  // Status-based blockers
  if (status === TASK_STATUS.REVIEW) {
    return BLOCKER_TYPES.EXPERT;
  }

  if (status === TASK_STATUS.WAITING || status === TASK_STATUS.FILING) {
    return BLOCKER_TYPES.AUTHORITY;
  }

  return BLOCKER_TYPES.NONE;
}

/**
 * Calculate next action based on status and blocker
 */
function calculateNextAction(status, blocker, serviceName, documents, requiredDocs) {
  if (status === TASK_STATUS.DONE) {
    return { action: 'Download certificate', urgent: false };
  }

  if (status === TASK_STATUS.CANCELLED) {
    return { action: 'View details', urgent: false };
  }

  if (blocker === BLOCKER_TYPES.USER) {
    const uploadedNames = documents.map(d => d.file_name?.toLowerCase() || '');
    const missingDoc = requiredDocs
      .filter(d => d.required)
      .find(d => !uploadedNames.some(name => name.includes(d.name.toLowerCase().split(' ')[0])));
    
    if (missingDoc) {
      return { action: `Upload ${missingDoc.name}`, urgent: true };
    }
    return { action: 'Upload required documents', urgent: true };
  }

  if (blocker === BLOCKER_TYPES.EXPERT) {
    return { action: 'Awaiting expert review', urgent: false };
  }

  if (blocker === BLOCKER_TYPES.AUTHORITY) {
    return { action: 'Awaiting authority response', urgent: false };
  }

  // Default actions by status
  const actionMap = {
    [TASK_STATUS.INTAKE]: { action: 'Complete intake form', urgent: true },
    [TASK_STATUS.REVIEW]: { action: 'Under review', urgent: false },
    [TASK_STATUS.FILING]: { action: 'Being filed', urgent: false },
    [TASK_STATUS.WAITING]: { action: 'Awaiting response', urgent: false }
  };

  return actionMap[status] || { action: 'View details', urgent: false };
}

/**
 * Calculate deadline dates
 */
function calculateDeadlines(serviceName, createdAt) {
  const config = SERVICE_DEADLINES[serviceName] || DEFAULT_DEADLINE;
  const created = new Date(createdAt);
  
  const expectedCompletion = new Date(created);
  expectedCompletion.setDate(expectedCompletion.getDate() + config.expectedDays);
  
  const filingDeadline = new Date(created);
  filingDeadline.setDate(filingDeadline.getDate() + config.filingDeadline);

  return {
    expectedCompletion: expectedCompletion.toISOString(),
    filingDeadline: filingDeadline.toISOString(),
    daysRemaining: Math.max(0, Math.ceil((expectedCompletion - new Date()) / (1000 * 60 * 60 * 24)))
  };
}

/**
 * Transform an order into a task
 */
export function orderToTask(order, documents = []) {
  const taskStatus = mapOrderStatusToTaskStatus(order.status);
  const requiredDocs = SERVICE_DOCUMENTS[order.service_name] || DEFAULT_DOCUMENTS;
  const taskDocuments = documents.filter(d => d.order_id === order.id);
  const blocker = determineBlocker(taskStatus, taskDocuments, requiredDocs);
  const nextAction = calculateNextAction(taskStatus, blocker, order.service_name, taskDocuments, requiredDocs);
  const deadlines = calculateDeadlines(order.service_name, order.created_at);

  // Calculate document checklist status
  const documentChecklist = requiredDocs.map(doc => {
    const uploadedNames = taskDocuments.map(d => d.file_name?.toLowerCase() || '');
    const isUploaded = uploadedNames.some(name => 
      name.includes(doc.name.toLowerCase().split(' ')[0])
    );
    return {
      ...doc,
      uploaded: isUploaded
    };
  });

  return {
    id: order.id,
    serviceName: order.service_name,
    serviceId: order.service_id,
    status: taskStatus,
    statusConfig: STATUS_CONFIG[taskStatus],
    blocker,
    blockerConfig: blocker ? BLOCKER_CONFIG[blocker] : null,
    nextAction,
    amount: order.amount,
    createdAt: order.created_at,
    updatedAt: order.updated_at,
    deadlines,
    documents: taskDocuments,
    documentChecklist,
    documentsComplete: documentChecklist.filter(d => d.required).every(d => d.uploaded),
    notes: order.notes,
    paymentId: order.payment_id,
    // Progress percentage based on status
    progress: getProgressPercentage(taskStatus)
  };
}

/**
 * Get progress percentage based on status
 */
function getProgressPercentage(status) {
  const progressMap = {
    [TASK_STATUS.INTAKE]: 10,
    [TASK_STATUS.REVIEW]: 40,
    [TASK_STATUS.FILING]: 60,
    [TASK_STATUS.WAITING]: 80,
    [TASK_STATUS.DONE]: 100,
    [TASK_STATUS.CANCELLED]: 0
  };
  return progressMap[status] || 0;
}

/**
 * Transform multiple orders into tasks
 */
export function ordersToTasks(orders, documents = []) {
  return orders.map(order => orderToTask(order, documents));
}

/**
 * Get dashboard stats from tasks
 */
export function getTaskStats(tasks) {
  const activeTasks = tasks.filter(t => 
    t.status !== TASK_STATUS.DONE && 
    t.status !== TASK_STATUS.CANCELLED
  );
  
  const pendingActions = tasks.filter(t => 
    t.blocker === BLOCKER_TYPES.USER
  );

  const upcomingDeadlines = activeTasks.filter(t => 
    t.deadlines.daysRemaining <= 7
  );

  const completedTasks = tasks.filter(t => 
    t.status === TASK_STATUS.DONE
  );

  return {
    active: activeTasks.length,
    pendingActions: pendingActions.length,
    upcomingDeadlines: upcomingDeadlines.length,
    completed: completedTasks.length,
    total: tasks.length
  };
}

/**
 * Sort tasks by priority (user action required first, then by deadline)
 */
export function sortTasksByPriority(tasks) {
  return [...tasks].sort((a, b) => {
    // User blockers first
    if (a.blocker === BLOCKER_TYPES.USER && b.blocker !== BLOCKER_TYPES.USER) return -1;
    if (b.blocker === BLOCKER_TYPES.USER && a.blocker !== BLOCKER_TYPES.USER) return 1;
    
    // Then by deadline (urgent first)
    return a.deadlines.daysRemaining - b.deadlines.daysRemaining;
  });
}

/**
 * Filter tasks by status
 */
export function filterTasksByStatus(tasks, status) {
  if (!status || status === 'all') return tasks;
  return tasks.filter(t => t.status === status);
}

/**
 * Get upcoming compliance deadlines from tasks
 */
export function getUpcomingDeadlines(tasks, daysAhead = 30) {
  const now = new Date();
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() + daysAhead);

  return tasks
    .filter(t => t.status !== TASK_STATUS.DONE && t.status !== TASK_STATUS.CANCELLED)
    .map(t => ({
      taskId: t.id,
      serviceName: t.serviceName,
      deadline: t.deadlines.expectedCompletion,
      daysRemaining: t.deadlines.daysRemaining,
      status: t.status,
      isUrgent: t.deadlines.daysRemaining <= 3
    }))
    .filter(d => new Date(d.deadline) <= cutoff)
    .sort((a, b) => a.daysRemaining - b.daysRemaining);
}
